import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, addDoc, query, where, orderBy,
  serverTimestamp, doc, getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { BellAlertIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

async function loadFamilyMembers(currentUser) {
  const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
  const familyIds = userDoc.data()?.familyIds || [];
  if (familyIds.length === 0) return { members: [], familyIds: [] };

  const memberIdSet = new Set();
  for (const familyId of familyIds) {
    const famDoc = await getDoc(doc(db, 'families', familyId));
    if (famDoc.exists()) {
      (famDoc.data().memberIDs || []).forEach(id => memberIdSet.add(id));
    }
  }
  memberIdSet.delete(currentUser.uid);

  const members = [];
  for (const uid of memberIdSet) {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const d = snap.data();
      members.push({
        uid,
        displayName: d.displayName || d.email || 'Family Member',
        email: d.email || '',
        profileImageUrl: d.profileImageUrl || null,
      });
    }
  }
  members.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return { members, familyIds };
}

async function loadPlans(familyIds) {
  // Chapter Plans (global reading plans)
  const chapterSnap = await getDocs(collection(db, 'plans'));
  const chapterPlans = chapterSnap.docs.map(d => ({
    id: d.id,
    name: d.data().name || 'Unnamed Plan',
    type: 'chapter',
  }));

  // Passage Plans (family-scoped)
  const passagePlans = [];
  if (familyIds.length > 0) {
    const seen = new Set();
    for (const familyId of familyIds) {
      const snap = await getDocs(
        query(collection(db, 'passagePlans'), where('familyId', '==', familyId))
      );
      snap.docs.forEach(d => {
        if (!seen.has(d.id)) {
          seen.add(d.id);
          passagePlans.push({ id: d.id, name: d.data().name || 'Unnamed Plan', type: 'passage' });
        }
      });
    }
    // Also load public passage plans
    const pubSnap = await getDocs(
      query(collection(db, 'passagePlans'), where('visibility', '==', 'public'))
    );
    pubSnap.docs.forEach(d => {
      if (!seen.has(d.id)) {
        seen.add(d.id);
        passagePlans.push({ id: d.id, name: d.data().name || 'Unnamed Plan', type: 'passage' });
      }
    });
  }

  const all = [
    ...chapterPlans.sort((a, b) => a.name.localeCompare(b.name)),
    ...passagePlans.sort((a, b) => a.name.localeCompare(b.name)),
  ];
  return all;
}

export default function RemindersPage() {
  const { currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [familyIds, setFamilyIds] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUid, setExpandedUid] = useState(null);
  // Which reminder mode: 'general' | 'plan'
  const [mode, setMode] = useState({});
  // Selected plan per member uid
  const [selectedPlan, setSelectedPlan] = useState({});
  const [lastSent, setLastSent] = useState({});
  const [sending, setSending] = useState({});
  const [justSent, setJustSent] = useState({});

  useEffect(() => {
    if (!currentUser) return;
    loadFamilyMembers(currentUser).then(({ members, familyIds }) => {
      setMembers(members);
      setFamilyIds(familyIds);
      return loadPlans(familyIds);
    }).then(setPlans).catch(e => console.error('Reminders load:', e)).finally(() => setLoading(false));

    // Load recent reminders to know cooldown state
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000);
    getDocs(query(
      collection(db, 'reminders'),
      where('fromUserId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    )).then(snap => {
      const map = {};
      snap.docs.forEach(d => {
        const data = d.data();
        const ts = data.createdAt?.toDate?.() || new Date(data.createdAt);
        if (ts > since && !map[data.toUserId]) map[data.toUserId] = ts.toISOString();
      });
      setLastSent(map);
    }).catch(() => {});
  }, [currentUser]);

  const canRemind = (uid) => {
    if (!lastSent[uid]) return true;
    return Date.now() - new Date(lastSent[uid]).getTime() > 24 * 60 * 60 * 1000;
  };

  const cooldownLabel = (uid) => {
    if (!lastSent[uid]) return '';
    const ms = 24 * 60 * 60 * 1000 - (Date.now() - new Date(lastSent[uid]).getTime());
    const h = Math.ceil(ms / (60 * 60 * 1000));
    return `Again in ${h}h`;
  };

  const toggleExpand = (uid) => {
    setExpandedUid(prev => prev === uid ? null : uid);
    setMode(m => ({ ...m, [uid]: m[uid] || 'general' }));
  };

  const sendReminder = useCallback(async (member, planName = null) => {
    if (!canRemind(member.uid) || sending[member.uid]) return;
    setSending(s => ({ ...s, [member.uid]: true }));
    try {
      await addDoc(collection(db, 'reminders'), {
        fromUserId: currentUser.uid,
        fromName: currentUser.displayName || currentUser.email || 'A family member',
        toUserId: member.uid,
        toUserName: member.displayName,
        reminderType: planName ? 'plan' : 'general',
        planName: planName || null,
        createdAt: serverTimestamp(),
        status: 'pending',
      });
      setLastSent(s => ({ ...s, [member.uid]: new Date().toISOString() }));
      setJustSent(s => ({ ...s, [member.uid]: true }));
      setTimeout(() => setJustSent(s => ({ ...s, [member.uid]: false })), 3000);
      setExpandedUid(null);
    } catch (e) {
      console.error('Reminders: send failed', e);
      alert('Could not send reminder. Please try again.');
    } finally {
      setSending(s => ({ ...s, [member.uid]: false }));
    }
  }, [currentUser, sending]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-cerulean border-t-transparent" />
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center px-4">
        <BellAlertIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-1">No family members yet</h2>
        <p className="text-sm text-gray-500">Invite someone to your family to send them a recording reminder.</p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-space-cadet dark:text-white flex items-center gap-2">
          <BellAlertIcon className="h-6 w-6 text-cerulean" />
          Reminders
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Tap a family member to send them a recording nudge.
        </p>
      </div>

      <div className="space-y-2">
        {members.map(member => {
          const expanded = expandedUid === member.uid;
          const ok = canRemind(member.uid);
          const busy = sending[member.uid];
          const sent = justSent[member.uid];
          const memberMode = mode[member.uid] || 'general';
          const plan = selectedPlan[member.uid] || '';

          return (
            <div
              key={member.uid}
              className="rounded-2xl bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 overflow-hidden"
            >
              {/* Header row — always visible, click to expand */}
              <button
                onClick={() => toggleExpand(member.uid)}
                className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                {/* Avatar */}
                <div className="h-10 w-10 shrink-0 rounded-full bg-cerulean/20 flex items-center justify-center overflow-hidden">
                  {member.profileImageUrl ? (
                    <img src={member.profileImageUrl} alt="" className="h-10 w-10 object-cover" />
                  ) : (
                    <span className="text-sm font-bold text-cerulean">
                      {member.displayName[0].toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Name + status */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {member.displayName}
                  </p>
                  {sent ? (
                    <p className="text-xs text-green-600 dark:text-green-400">✓ Reminder sent!</p>
                  ) : !ok ? (
                    <p className="text-xs text-gray-400">{cooldownLabel(member.uid)}</p>
                  ) : (
                    <p className="text-xs text-gray-400">Tap to send a reminder</p>
                  )}
                </div>

                {/* Chevron */}
                {expanded
                  ? <ChevronUpIcon className="h-4 w-4 text-gray-400 shrink-0" />
                  : <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
                }
              </button>

              {/* Expanded panel */}
              {expanded && (
                <div className="border-t border-gray-100 dark:border-white/10 px-4 py-4 space-y-4">

                  {/* Mode tabs */}
                  <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 text-sm">
                    {[
                      { key: 'general', label: 'General nudge' },
                      { key: 'plan', label: 'About a plan' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => setMode(m => ({ ...m, [member.uid]: key }))}
                        className={`flex-1 py-2 font-medium transition-colors ${
                          memberMode === key
                            ? 'bg-cerulean text-white'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* General nudge */}
                  {memberMode === 'general' && (
                    <div>
                      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                        They'll receive: <span className="italic">"{currentUser.displayName || 'A family member'} is reminding you to record on the Lineage Bible app!"</span>
                      </p>
                      <SendButton
                        ok={ok}
                        busy={busy}
                        cooldown={cooldownLabel(member.uid)}
                        onPress={() => sendReminder(member, null)}
                      />
                    </div>
                  )}

                  {/* Plan-specific */}
                  {memberMode === 'plan' && (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Choose a plan to encourage them about:
                      </p>
                      {plans.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No plans found.</p>
                      ) : (
                        <select
                          value={plan}
                          onChange={e => setSelectedPlan(s => ({ ...s, [member.uid]: e.target.value }))}
                          className="w-full rounded-xl border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cerulean"
                        >
                          <option value="">Select a plan…</option>
                          {plans.map(p => (
                            <option key={p.id} value={p.name}>{p.name}</option>
                          ))}
                        </select>
                      )}
                      {plan && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          They'll receive: <span className="italic">"{currentUser.displayName || 'A family member'} is encouraging you to keep recording the "{plan}" plan! 📖"</span>
                        </p>
                      )}
                      <SendButton
                        ok={ok && !!plan}
                        busy={busy}
                        cooldown={!plan ? 'Pick a plan first' : cooldownLabel(member.uid)}
                        onPress={() => sendReminder(member, plan)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-center text-xs text-gray-400">
        You can send each person one reminder per 24 hours.
      </p>
    </div>
  );
}

function SendButton({ ok, busy, cooldown, onPress }) {
  return (
    <button
      onClick={onPress}
      disabled={!ok || busy}
      className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
        ok
          ? 'bg-cerulean text-white hover:bg-cerulean/90'
          : 'bg-gray-100 text-gray-400 dark:bg-white/5 dark:text-gray-500 cursor-not-allowed'
      }`}
    >
      {busy ? (
        <span className="flex items-center justify-center gap-2">
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          Sending…
        </span>
      ) : ok ? 'Send Reminder' : cooldown}
    </button>
  );
}
